import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import { Link } from '@tiptap/extension-link';
import { Mention } from '@tiptap/extension-mention';
import { Placeholder } from '@tiptap/extension-placeholder';
import { TextAlign } from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import { EditorContent, useEditor } from '@tiptap/react';
import { StarterKit } from '@tiptap/starter-kit';
import { FC, ReactNode, useEffect } from 'react';

import { FontSize } from './font-size-extension';
import { TextColor } from './text-color-extension';
import { TiptapDoc } from './tiptap-types';
import { RichTextToolbar, TRichTextControl } from './toolbar';
import { buildMentionSuggestion, TFetchMentionUsers } from './use-mention-suggestion';

// Every existing consumer (comments, post composer, admin replies) keeps this
// exact toolbar unless it opts into a wider `toolbarControls` list.
const DEFAULT_TOOLBAR_CONTROLS: TRichTextControl[] = ['bold', 'italic', 'bulletList', 'link', 'emoji'];

export interface RichTextEditorProps {
  value: TiptapDoc | null;
  onChange: (json: TiptapDoc) => void;
  onChangeMention?: (mentionedUserIds: string[]) => void;
  onFetchMentionUsers?: TFetchMentionUsers;
  placeholder?: string;
  disabled?: boolean;
  /** Extra toolbar buttons appended after the built-in tools (bold/italic/link/emoji). */
  toolbarActions?: ReactNode;
  /** Include the @mention extension. Default true; set false to disable mentions entirely. */
  enableMentions?: boolean;
  /**
   * Which toolbar controls to show. Defaults to the historical fixed set
   * (bold/italic/bulletList/link/emoji) so every existing consumer is
   * unaffected. Pass a wider list (e.g. including 'alignment'/'fontSize') to
   * opt into the extra tiptap extensions those controls need.
   */
  toolbarControls?: TRichTextControl[];
  /** Content area min height. Defaults to '80px' (the historical comment-box size). */
  minHeight?: string;
  /** Content area max height, beyond which it scrolls. Defaults to '160px'. */
  maxHeight?: string;
}

// Strip `label` from mention nodes before persisting — only `id` is stored.
// Pure function with no dependencies, defined outside component to avoid
// recreating on every render.
const stripLabels = (nodes: TiptapDoc['content']): TiptapDoc['content'] =>
  nodes.map(node => {
    if (node.type === 'mention') {
      const { label: _label, ...attrs } = (node.attrs ?? {}) as Record<string, unknown>;
      return { ...node, attrs };
    }
    return node.content ? { ...node, content: stripLabels(node.content) } : node;
  });

export const RichTextEditor: FC<RichTextEditorProps> = ({
  value,
  onChange,
  onChangeMention,
  onFetchMentionUsers,
  placeholder = 'Write a comment...',
  disabled = false,
  toolbarActions,
  enableMentions = true,
  toolbarControls = DEFAULT_TOOLBAR_CONTROLS,
  minHeight = '80px',
  maxHeight = '160px',
}) => {
  const theme = useTheme();
  const enableAlignment = toolbarControls.includes('alignment');
  const enableFontSize = toolbarControls.includes('fontSize');
  const enableTextColor = toolbarControls.includes('textColor');
  const needsTextStyle = enableFontSize || enableTextColor;

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // bulletList + listItem stay enabled so the toolbar's bullet-list
        // button works; ordered lists remain off.
        orderedList: false,
        blockquote: false,
        codeBlock: false,
        code: false,
        strike: false,
        horizontalRule: false,
        heading: false,
        // StarterKit bundles its own Link in v3; disable it so the explicitly
        // configured Link below (openOnClick: false, target/rel) takes effect
        // instead of being dropped as a duplicate extension.
        link: false,
      }),
      Placeholder.configure({ placeholder }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { target: '_blank', rel: 'noopener noreferrer' },
      }),
      // Mentions are opt-out: consumers that don't want @-mentions (e.g. admin
      // notes) pass enableMentions={false} so typing '@' does nothing special.
      ...(enableMentions
        ? [
            Mention.configure({
              HTMLAttributes: {
                class: 'mention',
              },
              // Show label in editor; label is stripped before persisting
              renderHTML({ options, node }) {
                return [
                  'span',
                  { ...options.HTMLAttributes, 'data-id': node.attrs.id },
                  `@${(node.attrs.label as string | undefined) ?? (node.attrs.id as string)}`,
                ];
              },
              suggestion: buildMentionSuggestion(onFetchMentionUsers ?? (() => Promise.resolve([]))),
            }),
          ]
        : []),
      ...(enableAlignment ? [TextAlign.configure({ types: ['paragraph'] })] : []),
      // FontSize/TextColor's global attributes both ride on TextStyle's mark —
      // register it once, then each attribute extension only if its control is on.
      ...(needsTextStyle ? [TextStyle] : []),
      ...(enableFontSize ? [FontSize] : []),
      ...(enableTextColor ? [TextColor] : []),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      const json = ed.getJSON() as TiptapDoc;
      onChange({ ...json, content: stripLabels(json.content) });

      if (onChangeMention) {
        const mentionIds: string[] = [];
        ed.state.doc.descendants(node => {
          if (node.type.name === 'mention') {
            const id = node.attrs.id as string;
            if (id && !mentionIds.includes(id)) {
              mentionIds.push(id);
            }
          }
        });
        onChangeMention(mentionIds);
      }
    },
  });

  // Sync external value changes (e.g. form reset) into editor.
  // Compare against the label-stripped version of current editor content so
  // that stripping labels in onChange doesn't trigger a re-sync (which would
  // replace the display names with UUIDs in the editor).
  useEffect(() => {
    if (!editor) {
      return;
    }
    const current = editor.getJSON() as TiptapDoc;
    const strippedCurrent = { ...current, content: stripLabels(current.content) };
    if (JSON.stringify(value) !== JSON.stringify(strippedCurrent)) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [value, editor]);

  // Sync disabled state
  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [disabled, editor]);

  return (
    <Box
      sx={{
        border: theme => `1px solid ${theme.palette.divider}`,
        borderRadius: '5px',
        overflow: 'hidden',
        backgroundColor: theme.palette.common.white,
        opacity: disabled ? 0.6 : 1,
        '& .mention': {
          color: theme.palette.primary.main,
          fontWeight: 600,
          backgroundColor: `${theme.palette.primary.main}22`,
          borderRadius: '4px',
          px: '4px',
          py: '2px',
          cursor: 'default',
          display: 'inline',
        },
        '& .ProseMirror': {
          minHeight,
          maxHeight,
          overflowY: 'auto',
          px: '15px',
          py: '12px',
          outline: 'none',
          fontSize: theme.typography.body2.fontSize,
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.text.primary,
          lineHeight: 1.5,
          '&:focus-visible': {
            outline: 'none',
          },
          '& p.is-editor-empty:first-of-type::before': {
            content: 'attr(data-placeholder)',
            float: 'left',
            color: theme.palette.grey[800],
            fontWeight: 400,
            pointerEvents: 'none',
            height: 0,
          },
          '& p': {
            margin: 0,
          },
          '& ul': {
            margin: '4px 0',
            paddingLeft: '1.5em',
            listStyle: 'disc',
          },
          '& li': {
            margin: 0,
          },
          '& li > p': {
            margin: 0,
          },
          '& a': {
            color: theme.palette.primary.main,
            textDecoration: 'underline',
            cursor: 'pointer',
          },
        },
      }}
    >
      <RichTextToolbar editor={editor} extraActions={toolbarActions} controls={toolbarControls} />
      <EditorContent editor={editor} />
    </Box>
  );
};
